// ---------------------------------------------------------------------------
// Course Component: Exercise Block — Instructions + Student Upload
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

export interface ExerciseBlockProps {
  title: string;
  instructions: string;
  /** Accepted file types for upload */
  acceptedFileTypes?: string[];
  maxFileSizeMb?: number;
  /** Whether submission is required to mark lesson complete */
  required?: boolean;
  /** Rubric/grading criteria shown to student */
  rubric?: string;
}

export const EXERCISE_BLOCK_DEF = {
  type: "exercise-block" as const,
  label: "Exercise",
  category: "course",
  defaultProps: {
    title: "Exercise",
    instructions: "",
    acceptedFileTypes: [".pdf", ".doc", ".docx", ".jpg", ".png", ".zip"],
    maxFileSizeMb: 50,
    required: true,
  } satisfies Partial<ExerciseBlockProps>,
};

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as ExerciseBlockProps;
  const acceptedFileTypes = Array.isArray(p.acceptedFileTypes)
    ? p.acceptedFileTypes
    : [];

  const fileTypesLabel = acceptedFileTypes.length > 0
    ? acceptedFileTypes.join(", ")
    : "Any file type";

  return `<section class="ease-section ease-course-exercise" aria-label="${attr(p.title ?? "Exercise")}">
  <div class="ease-course-exercise__card">
    <header class="ease-course-exercise__header">
      <div>
        <h2 class="ease-course-exercise__title">${text(p.title ?? "Exercise")}</h2>
        ${p.instructions ? `<p class="ease-course-exercise__instructions">${text(p.instructions)}</p>` : ""}
      </div>
      ${p.required !== false ? '<span class="ease-course-exercise__badge">Required</span>' : ""}
    </header>
    ${p.rubric ? `<div class="ease-course-exercise__rubric"><strong>Rubric</strong><p>${text(p.rubric)}</p></div>` : ""}
    <div class="ease-course-exercise__upload" aria-label="${attr(`Upload area accepting ${fileTypesLabel}`)}">
      <p>Upload accepted work</p>
      <p>Accepted: ${text(fileTypesLabel)}</p>
      <p>Max file size: ${text(String(p.maxFileSizeMb ?? 50))} MB</p>
    </div>
  </div>
</section>`;
}

registerRenderer("exercise-block", { render });
